import { api, usarCredencial } from '../api';
import type { LoginResponse } from '../api';

/**
 * Dónde vive la sesión de una persona (KAN-142).
 *
 * # EL TOKEN NO SE GUARDA EN NINGÚN LADO PERSISTENTE
 *
 * Vive en la variable de módulo {@link estado} y en ningún otro lugar: ni
 * `localStorage`, ni `sessionStorage`, ni cookie escrita por JavaScript, ni la
 * URL. Cualquier script que llegue a ejecutarse en la página —una dependencia
 * comprometida, una extensión, un XSS— lee esos storages enteros con una línea,
 * y con este token se **emiten comprobantes fiscales**, que no se borran: se
 * corrigen con nota de crédito. Una variable de módulo no es inexpugnable
 * (quien ya ejecuta código en la página tiene otras formas), pero no regala la
 * credencial a cualquier script que pase, y sobre todo **no la deja escrita
 * cuando la pestaña se cierra**.
 *
 * El precio, aceptado a conciencia: **recargar la página pide login de nuevo.**
 *
 * Si algún día hace falta "seguir logueado" entre recargas, la respuesta NO es
 * mover esto a storage sino una cookie `HttpOnly` + `SameSite` puesta por el
 * backend —que el JavaScript de la página no puede leer—. Eso es un cambio de
 * backend (hoy el login devuelve el token en el cuerpo y la auth es por header
 * `Bearer`) y es una decisión aparte, con su propia tarjeta. No se resuelve acá.
 *
 * # POR QUÉ NO ES ESTADO DE REACT
 *
 * Porque quien cierra la sesión muchas veces **no es** React: es la respuesta
 * `401` de un pedido cualquiera (ver {@link cerrarPorRechazo}). Este módulo es
 * un almacén externo con suscripción, y los componentes se enganchan con
 * `useSyncExternalStore` (ver `useSesion`), que es exactamente la herramienta
 * que React da para estado que vive afuera.
 */

/**
 * La sesión abierta. **Derivada del contrato**, no escrita a mano: si el
 * backend renombra un campo de `LoginResponse`, esto deja de compilar. Lo único
 * propio es `venceEn`, que acá es un `Date` y en el contrato un string ISO.
 */
export type Sesion = Omit<LoginResponse, 'venceEn'> & { readonly venceEn: Date };

/**
 * Por qué se cerró la última sesión.
 *
 * - `salida`: la persona apretó "Salir". Se la manda al login y listo.
 * - `credencial_rechazada`: el backend contestó `401`. **No es lo mismo**: la
 *   persona estaba trabajando y puede tener un formulario a medio llenar, así
 *   que la pantalla no se desmonta (ver `RutaProtegida`).
 */
export type MotivoDeCierre = 'salida' | 'credencial_rechazada';

export interface EstadoDeSesion {
  /** La sesión abierta, o `null` si no hay ninguna. */
  readonly sesion: Sesion | null;
  /**
   * Motivo del último cierre. `null` mientras hay sesión abierta y también
   * cuando nunca hubo una —recién cargada la página, por ejemplo—. Es lo que
   * distingue "nunca entró" de "se le cayó la sesión encima del formulario".
   */
  readonly cierre: MotivoDeCierre | null;
}

const SIN_SESION: EstadoDeSesion = { sesion: null, cierre: null };

let estado: EstadoDeSesion = SIN_SESION;

const escuchas = new Set<() => void>();

/**
 * El estado actual. Devuelve **siempre la misma referencia** mientras nada
 * cambie, que es lo que `useSyncExternalStore` exige para no re-renderizar en
 * loop.
 */
export function estadoDeSesion(): EstadoDeSesion {
  return estado;
}

/** La sesión abierta, o `null`. Atajo de {@link estadoDeSesion}. */
export function sesionActual(): Sesion | null {
  return estado.sesion;
}

/**
 * El token a mandar en `Authorization`, o `null` si no hay sesión. Es lo que se
 * le instala al cliente HTTP: el cliente **no guarda** el token, se lo pregunta
 * a esta función en cada pedido. Por eso cerrar la sesión desarma el header sin
 * que nadie más tenga que enterarse.
 */
export function tokenDeSesion(): string | null {
  return estado.sesion?.token ?? null;
}

/** Se suscribe a los cambios. Devuelve la función para darse de baja. */
export function suscribirse(escucha: () => void): () => void {
  escuchas.add(escucha);
  return () => {
    escuchas.delete(escucha);
  };
}

function publicar(nuevo: EstadoDeSesion): void {
  estado = nuevo;
  for (const escucha of [...escuchas]) {
    escucha();
  }
}

/** Abre la sesión con lo que devolvió `POST /api/v1/auth/login`. */
export function abrirSesion(respuesta: LoginResponse): Sesion {
  const sesion: Sesion = { ...respuesta, venceEn: new Date(respuesta.venceEn) };
  publicar({ sesion, cierre: null });
  return sesion;
}

/**
 * Actualiza nombre y rol sin tocar el token, con lo que devolvió
 * `GET /api/v1/auth/yo`.
 *
 * Existe porque **la autorización sale de la base, no de los claims del token**:
 * a alguien le pueden bajar el rol de ADMIN a OPERADOR mientras su JWT sigue
 * vigente. Sin esto el menú seguiría mostrando administración hasta el
 * vencimiento —el backend lo rechazaría igual, pero la pantalla estaría
 * mintiendo—.
 */
export function refrescarIdentidad(identidad: {
  nombre: string;
  email: string;
  rol: Sesion['rol'];
}): void {
  const abierta = estado.sesion;
  if (!abierta) {
    return;
  }
  if (
    abierta.nombre === identidad.nombre &&
    abierta.email === identidad.email &&
    abierta.rol === identidad.rol
  ) {
    return; // Sin cambios: no se publica, para no re-renderizar de gusto.
  }
  publicar({ sesion: { ...abierta, ...identidad }, cierre: null });
}

/** Cierra la sesión porque la persona apretó "Salir". */
export function cerrarSesion(): void {
  publicar({ sesion: null, cierre: 'salida' });
}

/**
 * Cierra la sesión porque el backend rechazó la credencial.
 *
 * **Un `401` puede llegar mucho antes de que venza el token y no es un bug.**
 * El backend lee del JWT solo el id y relee al usuario contra la base en cada
 * request: si un admin desactiva a alguien o le cambia el rol, el efecto es
 * inmediato. Eso es lo que hace las veces de revocación —un JWT no se puede
 * revocar de otra forma— y por eso el front lo trata como un caso normal.
 */
function cerrarPorRechazo(): void {
  if (!estado.sesion) {
    return; // No había nada que cerrar.
  }
  publicar({ sesion: null, cierre: 'credencial_rechazada' });
}

/** Vuelve al estado inicial, sin motivo de cierre. Para los tests. */
export function olvidarTodo(): void {
  publicar(SIN_SESION);
}

/** Milisegundos que le quedan al token. Negativo si ya venció. */
export function milisegundosRestantes(sesion: Sesion, ahora: number = Date.now()): number {
  return sesion.venceEn.getTime() - ahora;
}

/**
 * ¿Es la ruta del login? Un `401` ahí significa "esas credenciales no sirven",
 * no "tu sesión se cayó": no tiene que cerrar la sesión de nadie.
 */
function esElLogin(url: string): boolean {
  try {
    return new URL(url).pathname === '/api/v1/auth/login';
  } catch {
    return false;
  }
}

/**
 * Enganche con el cliente HTTP. **Se hace al importar el módulo, a propósito**:
 * así no existe la posibilidad de usar la sesión y olvidarse de conectar el
 * `401`. Las dos mitades tienen que estar siempre juntas.
 *
 * 1. De dónde sale la credencial: de {@link tokenDeSesion}.
 * 2. Qué pasa con un `401`: cierra la sesión, **venga del endpoint que venga**.
 *    Está acá y no en cada pantalla porque una sola pantalla que se olvide de
 *    manejarlo deja a la app mostrando datos de alguien que ya no tiene acceso.
 *
 * No se toca `cliente.ts`: se usan los dos huecos que KAN-141 dejó para esto
 * (`usarCredencial` y el `use()` de openapi-fetch).
 */
usarCredencial(tokenDeSesion);
api.use({
  onResponse({ request, response }) {
    if (response.status === 401 && !esElLogin(request.url)) {
      cerrarPorRechazo();
    }
    return undefined; // Se devuelve la respuesta original, sin tocarla.
  },
});
